/**
 * AR Collections — per-customer drill drawer.
 * 4 tabs: AI reminder / Status / Invoices / History.
 * Extracted from FinanceCollections.jsx in Iter 82.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Download, Send, Sparkles, RefreshCw, Mail, MessageSquare, MessageCircle, AlertCircle, CheckCircle2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { fmt, STATUS_META, CHANNEL_ICON } from "./constants";
import { apiErrorMessage } from "@/lib/apiError";

export default function CustomerDrill({ wsId, clientId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("compose");
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [channel, setChannel] = useState("email");
  const [tone, setTone] = useState("friendly");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [selectedInvs, setSelectedInvs] = useState([]);
  const [status, setStatus] = useState("in_followup");
  const [promisedDate, setPromisedDate] = useState("");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/finance/workspaces/${wsId}/ar/collections/${clientId}`);
      setData(d);
      setSelectedInvs((d.unpaid_invoices || []).map((i) => i.id));
      const f = d.followup || {};
      setStatus(f.status || "in_followup");
      setPromisedDate(f.promised_payment_date || "");
      setNotes(f.notes || "");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setLoading(false); }
  }, [wsId, clientId]);

  useEffect(() => { load(); }, [load]);

  const runDraft = async () => {
    setDrafting(true);
    try {
      const { data: d } = await api.post(`/finance/workspaces/${wsId}/ar/reminders/draft`,
        { client_id: clientId, invoice_ids: selectedInvs, channel, tone });
      setDraft(d);
      setSubject(d.subject || "");
      setBodyText(d.body_text || "");
      setBodyHtml(d.body_html || "");
      toast.success("Draft ready");
    } catch (e) { toast.error(apiErrorMessage(e, "Draft failed")); }
    finally { setDrafting(false); }
  };

  const send = async () => {
    if (!bodyText.trim()) { toast.error("Body cannot be empty"); return; }
    setSending(true);
    try {
      const { data: d } = await api.post(`/finance/workspaces/${wsId}/ar/reminders/send`, {
        client_id: clientId, invoice_ids: selectedInvs, channel, tone,
        subject: subject || undefined, body_text: bodyText,
        body_html: bodyHtml || undefined, attach_pdf: attachPdf,
      });
      const st = d?.result?.status || (d?.result?.sent ? "sent" : "logged");
      if (st === "sent") toast.success(`Reminder sent via ${channel}`);
      else if (st === "logged_only") toast.info("Reminder logged (email provider not configured)");
      else if (st === "skipped") toast.warning(`Skipped: ${d.result.reason}`);
      else if (st === "failed") toast.error(`Send failed: ${d.result.reason || d.result.error}`);
      else toast.success("Reminder recorded");
      load(); onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setSending(false); }
  };

  const saveFollowup = async () => {
    try {
      await api.post(`/finance/workspaces/${wsId}/ar/followups`, {
        client_id: clientId, status,
        promised_payment_date: promisedDate || null,
        notes: notes || null,
      });
      toast.success("Follow-up updated");
      load(); onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(
        `/finance/workspaces/${wsId}/ar/statement.pdf?client_id=${clientId}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `statement-${data?.client?.name || "customer"}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  if (loading) return <div className="text-sm text-muted-foreground" data-testid="drill-loading">Loading…</div>;
  if (!data) return null;

  const outstanding = data.outstanding;
  const invs = data.unpaid_invoices || [];
  const contact = data.contact || {};

  return (
    <div className="space-y-5">
      <Card><CardContent className="p-4 space-y-2" data-testid="drill-summary">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-muted-foreground">Outstanding</div>
            <div className="text-2xl font-mono text-rose-600" data-testid="drill-outstanding">{fmt(outstanding)}</div>
          </div>
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="drill-download-pdf">
            <Download className="w-4 h-4 mr-1" />PDF statement
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {contact.name && <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" />{contact.name}</span>}
          {contact.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{contact.email}</span>}
          {contact.phone && <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{contact.phone}</span>}
        </div>
      </CardContent></Card>

      <div className="flex gap-1 border-b" data-testid="drill-tabs">
        {["compose", "status", "invoices", "history"].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm capitalize border-b-2 -mb-px ${tab === t ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`drill-tab-${t}`}
          >
            {t === "compose" ? "AI reminder" : t}
          </button>
        ))}
      </div>

      {tab === "compose" && (
        <div className="space-y-3" data-testid="drill-compose">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="drill-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email"><Mail className="w-3 h-3 inline mr-1" />Email</SelectItem>
                  <SelectItem value="whatsapp"><MessageCircle className="w-3 h-3 inline mr-1" />WhatsApp</SelectItem>
                  <SelectItem value="sms"><MessageSquare className="w-3 h-3 inline mr-1" />SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="drill-tone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="firm">Firm</SelectItem>
                  <SelectItem value="final">Final notice</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={runDraft} disabled={drafting} data-testid="drill-draft-btn">
            {drafting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {drafting ? "Drafting…" : draft ? "Regenerate draft" : "AI-draft reminder"}
          </Button>

          {channel === "email" && (
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="drill-subject" />
            </div>
          )}
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea rows={10} value={bodyText} onChange={(e) => setBodyText(e.target.value)} data-testid="drill-body" />
          </div>
          {channel === "email" && (
            <label className="flex items-center gap-2 text-sm" data-testid="drill-attach-pdf">
              <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />
              Attach PDF statement + inline HTML ledger
            </label>
          )}
          <Button onClick={send} disabled={sending || !bodyText.trim()} data-testid="drill-send-btn">
            {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {sending ? "Sending…" : `Send via ${channel}`}
          </Button>
        </div>
      )}

      {tab === "status" && (
        <div className="space-y-3" data-testid="drill-status-panel">
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="drill-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Promised payment date</Label>
            <Input type="date" value={promisedDate} onChange={(e) => setPromisedDate(e.target.value)} data-testid="drill-promised-date" />
            <div className="text-[11px] text-muted-foreground mt-1">
              If promised → we auto-move to <b>broken promise</b> 1 day after this date if balance remains.
            </div>
          </div>
          <div>
            <Label className="text-xs">Internal notes</Label>
            <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Spoke to Priya on 25 Jun — payment approval routed to CFO." data-testid="drill-notes" />
          </div>
          <Button onClick={saveFollowup} data-testid="drill-save-followup">
            <CheckCircle2 className="w-4 h-4 mr-2" />Save follow-up
          </Button>
        </div>
      )}

      {tab === "invoices" && (
        <div className="space-y-2" data-testid="drill-invoices">
          <div className="text-xs text-muted-foreground">Select invoices to include in the reminder + PDF statement.</div>
          {invs.length === 0 && <div className="text-sm text-muted-foreground">All caught up 🎉</div>}
          {invs.map((inv) => {
            const checked = selectedInvs.includes(inv.id);
            return (
              <label key={inv.id} className="flex items-center gap-3 border rounded p-2 text-sm cursor-pointer" data-testid={`drill-inv-${inv.id}`}>
                <input type="checkbox" checked={checked} onChange={(e) => {
                  if (e.target.checked) setSelectedInvs((prev) => [...prev, inv.id]);
                  else setSelectedInvs((prev) => prev.filter((x) => x !== inv.id));
                }} />
                <div className="flex-1">
                  <div className="font-medium">{inv.doc_no || inv.invoice_number || inv.id.slice(0, 8)}</div>
                  <div className="text-xs text-muted-foreground">
                    Due {inv.due_date || "—"} · {inv.days_overdue > 0 ? `${inv.days_overdue}d overdue` : "current"}
                  </div>
                </div>
                <div className="text-right font-mono">{fmt(inv.balance_due)}</div>
              </label>
            );
          })}
        </div>
      )}

      {tab === "history" && (
        <div className="space-y-2" data-testid="drill-history">
          {(data.reminders || []).length === 0 && (
            <div className="text-sm text-muted-foreground">No reminders sent yet.</div>
          )}
          {(data.reminders || []).map((r) => {
            const Icon = CHANNEL_ICON[r.channel] || Mail;
            const okTone = r.status === "sent" ? "text-emerald-600" : r.status === "failed" ? "text-rose-600" : "text-muted-foreground";
            return (
              <div key={r.id} className="border rounded p-3 text-sm space-y-1" data-testid={`drill-hist-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium capitalize">{r.channel}</span>
                    <Badge variant="outline" className="text-[10px]">{r.tone}</Badge>
                  </span>
                  <span className={`text-xs ${okTone}`}>
                    {r.status === "sent" ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : r.status === "failed" ? <AlertCircle className="w-3 h-3 inline mr-1" /> : null}
                    {r.status}
                  </span>
                </div>
                {r.subject && <div className="text-sm font-medium">{r.subject}</div>}
                <div className="text-xs text-muted-foreground">
                  {new Date(r.sent_at).toLocaleString()} · {r.sent_by_name || r.sent_by} · to {r.to}
                </div>
                <div className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">{r.body_text?.slice(0, 300)}</div>
                {r.error && <div className="text-xs text-rose-600">Error: {r.error}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
